import {
  Button,
  Progress,
  ScrollArea,
  Text,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from "@musa.tech/design-system";
import { Check, Download, Loader2 } from "lucide-react";

interface MtrBatchGridProps {
  totalBatches: number;
  batchSize: number;
  /** Rótulo do tipo de documento no resumo do lote. */
  documentLabel?: string;
  downloadedBatches: Set<number>;
  activeBatch: number | null;
  isDownloading: boolean;
  onDownloadBatch: (batch: number) => void;
}

/**
 * Grade compacta de lotes de download (chips numerados) com progresso geral e
 * ação principal para o fluxo sequencial. Fica dentro de uma altura fixa
 * (ScrollArea) para não estourar o modal quando há muitos lotes.
 */
export const MtrBatchGrid = ({
  totalBatches,
  batchSize,
  documentLabel = "MTR",
  downloadedBatches,
  activeBatch,
  isDownloading,
  onDownloadBatch,
}: MtrBatchGridProps) => {
  const doneCount = downloadedBatches.size;
  const allDone = doneCount === totalBatches;
  const nextBatch = Array.from({ length: totalBatches }).findIndex(
    (_, batch) => !downloadedBatches.has(batch),
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <Text variant="label" size="small" className="text-neutral-600">
          {allDone
            ? "Todos os lotes foram baixados"
            : `Lote ${doneCount + 1} de ${totalBatches}`}
        </Text>
        <Text variant="label" size="small" className="text-neutral-500">
          {batchSize} {documentLabel}s por lote
        </Text>
      </div>

      <Progress value={(doneCount / totalBatches) * 100} className="h-1.5" />

      {allDone ? (
        <div className="flex items-center gap-2 rounded-md bg-success-50 px-3 py-2">
          <Check className="h-4 w-4 text-success-500 flex-shrink-0" />
          <Text variant="label" size="small" className="text-success-600">
            Arquivo de todos os {totalBatches} lotes baixado com sucesso.
          </Text>
        </div>
      ) : (
        <Button
          variant="default"
          className="w-full"
          disabled={isDownloading}
          onClick={() => onDownloadBatch(nextBatch)}
        >
          {isDownloading && activeBatch === nextBatch ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          {isDownloading && activeBatch === nextBatch
            ? "Gerando arquivo..."
            : `Baixar lote ${nextBatch + 1}`}
        </Button>
      )}

      <TooltipProvider>
        <ScrollArea className="h-40 rounded-md border border-neutral-200 bg-neutral-50 p-2">
          <div className="grid grid-cols-7 sm:grid-cols-9 gap-1.5">
            {Array.from({ length: totalBatches }).map((_, batch) => {
              const isDone = downloadedBatches.has(batch);
              const isActive = isDownloading && activeBatch === batch;

              return (
                <Tooltip key={batch}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      disabled={isDownloading}
                      onClick={() => onDownloadBatch(batch)}
                      aria-label={`Lote ${batch + 1}${isDone ? " — baixado" : ""}`}
                      className={cn(
                        "flex h-8 w-8 items-center justify-center rounded-md text-xs font-medium transition-colors",
                        "disabled:cursor-not-allowed disabled:opacity-60",
                        isDone
                          ? "bg-success-500 text-white"
                          : isActive
                            ? "border border-primary-500 bg-primary-50 text-primary-600"
                            : "border border-neutral-200 bg-white text-neutral-600 hover:border-primary-400 hover:text-primary-600",
                      )}
                    >
                      {isDone ? (
                        <Check className="h-3.5 w-3.5" />
                      ) : isActive ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        batch + 1
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {isDone
                      ? `Lote ${batch + 1} — baixado`
                      : isActive
                        ? `Gerando lote ${batch + 1}...`
                        : `Baixar lote ${batch + 1}`}
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        </ScrollArea>
      </TooltipProvider>
    </div>
  );
};

export default MtrBatchGrid;
